"use client";

import { useEffect, useState } from "react";

/**
 * After the QR expiry passes we keep polling for this long: a buyer who paid
 * at the last second can still land on success while the provider settles.
 */
export const QRIS_GRACE_PERIOD_SECONDS = 5 * 60;

/** Fallback countdown when the server omits expiresAt (matches the real
 * provider QR lifetime). */
export const QRIS_FALLBACK_EXPIRY_SECONDS = 30 * 60;

export type QrisExpiryPhase = "active" | "grace" | "expired";

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Picks the latest of the provided ISO timestamps; a reissued QR always
 * extends the deadline past whatever a stale poll response reported.
 */
export function latestTimestamp(
  ...values: Array<string | null | undefined>
): number | null {
  const parsed = values
    .map(parseTimestamp)
    .filter((ms): ms is number => ms !== null);
  return parsed.length > 0 ? Math.max(...parsed) : null;
}

interface UseQrisCountdownArgs {
  /** Authoritative expiry from the server in epoch ms; null falls back locally. */
  serverDeadlineMs: number | null;
  /** Local fallback lifetime in seconds when the server omits expiresAt. */
  fallbackSeconds: number;
  /** Ticking stops on terminal payment states. */
  running: boolean;
}

export function useQrisCountdown({
  serverDeadlineMs,
  fallbackSeconds,
  running,
}: UseQrisCountdownArgs) {
  const [fallbackDeadline] = useState(
    () => Date.now() + fallbackSeconds * 1000,
  );
  const [now, setNow] = useState(() => Date.now());

  const deadline = serverDeadlineMs ?? fallbackDeadline;

  // Resync immediately when the deadline moves (first poll response with the
  // authoritative expiry, or a reissued QR) so the label never shows a stale
  // value for up to a second.
  useEffect(() => {
    setNow(Date.now());
  }, [deadline]);

  useEffect(() => {
    if (!running) {
      return;
    }

    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, [running]);

  const secondsLeft = Math.max(0, Math.ceil((deadline - now) / 1000));
  const graceSecondsLeft = Math.max(
    0,
    Math.ceil((deadline + QRIS_GRACE_PERIOD_SECONDS * 1000 - now) / 1000),
  );

  const phase: QrisExpiryPhase =
    secondsLeft > 0 ? "active" : graceSecondsLeft > 0 ? "grace" : "expired";

  return { secondsLeft, phase };
}
